export interface IDividend {
  dividend_id: string;
  company_id: string;
  company_symbol: string;
  dividend_cumdate: string;
  dividend_exdate: string;
  dividend_recdate: string;
  dividend_paydate: string;
  dividend_value: number;
  dividend_datahash: string;
  dividend_created: string;
  dividend_lock: string;
  dividend_lastupdate: string;
  dividend_iqp_id: string;
}

export interface IStockDividend {
  company: {
    id: string;
    symbol: string;
  };
  corp_action_active: boolean;
  created: string;
  dates: {
    cum: string;
    ex: string;
    payment: string;
    recording: string;
  };
  event_note: string;
  factor: string;
  id: string;
  iqp_id: string;
  last_update: string;
  new_price: string;
  new_share: string;
  ratios: {
    new: string;
    old: string;
    formatted: string;
  };
}

export interface IEconomic {
  econcal_id: string;
  econcal_date: string;
  econcal_time: string;
  econcal_month: string;
  econcal_item: string;
  econcal_actual: string;
  econcal_previous: string;
  econcal_forecast: string;
  econcal_lastdate: string;
}

export interface IStocksplitOrStockReverse {
  stocksplit_id: string;
  company_id: string;
  company_symbol: string;
  stocksplit_cumdate: string;
  stocksplit_exdate: string;
  stocksplit_recdate: string;
  stocksplit_new: string;
  stocksplit_old: string;
  stocksplit_factor: string;
  stocksplit_lock: string;
  stocksplit_created: string;
  stocksplit_new_price: string;
  stocksplit_lastupdate: string;
  stocksplit_new_share: string;
  stocksplit_iqp_id: string;
}

export interface IRightissue {
  rightissue_id: string;
  company_id: string;
  company_symbol: string;
  rightissue_cumdate: string;
  rightissue_exdate: string;
  rightissue_recdate: string;
  rightissue_trading_start: string;
  rightissue_trading_end: string;
  rightissue_old: string;
  rightissue_new: string;
  rightissue_factor: string;
  rightissue_price_factor: string;
  rightissue_price: string;
  rightissue_subdate: string;
  rightissue_created: string;
  rightissue_lock: string;
  rightissue_price_adj: string;
  rightissue_lastupdate: string;
  rightissue_new_share: string;
  rightissue_adj_factor: string;
  rightissue_iqp_id: string;
}

export interface IWarrant {
  wrant_id: string;
  company_id: string;
  company_symbol: string;
  wrant_serie: string;
  wrant_exc_price: string;
  wrant_trading_from: string;
  wrant_trading_end: string;
  wrant_exc_from: string;
  wrant_exc_end: string;
  wrant_total: string;
  wrant_lastupdate: string;
  wrant_iqp_id: string;
}

export interface IBonus {
  sahambonus_id: string;
  company_id: string;
  company_symbol: string;
  stocksplit_cumdate: string;
  stocksplit_exdate: string;
  stocksplit_recdate: string;
  stocksplit_paymentdate: string;
  stocksplit_old: string;
  stocksplit_new: string;
  stocksplit_factor: string;
  stocksplit_created: string;
  sahambonus_new_price: string;
  sahambonus_lastupdate: string;
  sahambonus_new_share: string;
  sahambonus_iqp_id: string;
}

export interface ITender {
  tender_id: string;
  company_id: string;
  tender_price: string;
  tender_shares: string;
  tender_percentage: string;
  tender_start: string;
  tender_end: string;
  tender_paydate: string;
  tender_created: string;
  tender_datahash: string;
  company_symbol: string;
  company_name: string;
}

export interface IRups {
  rups_id: string;
  company_id: string;
  company_symbol: string;
  rups_date: string;
  rups_venue: string;
  rups_created: string;
  rups_datahash: string;
  rups_iqp_id: string;
  rups_iqp_result?: null;
  rups_iqp_rec_dt: string;
  rups_iqp_revised_date: string;
  rups_iqp_type: string;
  rups_iqp_remark: string;
  rups_iqp_agenda: string;
}

export interface IPubex {
  puexp_id: string;
  company_id: string;
  company_symbol: string;
  puexp_date: string;
  puexp_time: string;
  puexp_venue: string;
  puexp_lastupdate: string;
  puexp_iqp_id: string;
}

export interface IIpoDataDetail {
  price: string;
  shares: string;
  percentage: string;
  offering_start: string;
  offering_end: string;
  allotment_date: string;
  refund_date: string;
}

export interface IIpo {
  ipo_id: string;
  company_symbol: string;
  company_name: string;
  ipo_listing_date: string;
  ipo_data: string;
  ipo_created: string;
  ipo_iqp_id: string;
  ipo_data_detail: IIpoDataDetail;
}

export interface IData {
  today: string;
  timezone: number;
  cash_dividend?: IDividend[] | null;
  economic?: IEconomic[] | null;
  stocksplit?: IStocksplitOrStockReverse[] | null;
  stock_reverse?: IStocksplitOrStockReverse[] | null;
  rightissue?: IRightissue[] | null;
  warrant?: IWarrant[] | null;
  bonus?: IBonus[] | null;
  tender?: ITender[] | null;
  rups?: IRups[] | null;
  pubex?: IPubex[] | null;
  ipo?: IIpo[] | null;
}

export interface ICalendarToday {
  message: string;
  data: IData;
  isLoading: boolean;
  error: string;
}

export interface ICalendarTabs {
  message: string;
  error: string;
  data: {
    timezone?: number;
    today?: string;
    dividend?: IDividend[] | null;
    economic?: IEconomic[] | null;
    stocksplit?: IStocksplitOrStockReverse[] | null;
    stock_reverse?: IStocksplitOrStockReverse[] | null;
    rightissue?: IRightissue[] | null;
    warrant?: IWarrant[] | null;
    bonus?: IBonus[] | null;
    tender?: ITender[] | null;
    rups?: IRups[] | null;
    pubex?: IPubex[] | null;
    ipo?: IIpo[] | null;
  };
  isLoading: boolean;
}

export enum CalendarTodayEnums {
  CUM_DATE = 'Cum Date',
  EX_DATE = 'Ex Date',
  PAY_DATE = 'Pay Date',
  TRADING_START = 'Trading Start',
  TRADING_END = 'Trading End',
  EXERCISE_START = 'Exercise Start',
  EXERCISE_END = 'Exercise End',
  OFFERING_START = 'Offering Start',
  OFFERING_END = 'Offering End',
}

export enum CalendarTodayDataIndexEnums {
  ECONCAL_EDITTED_SYMBOL = 'econcal_editted_symbol',
  ECONCAL_EDITTED_TIME = 'econcal_editted_time',
  COMPANY_SYMBOL = 'company_symbol',
  STOCKSPLIT_ADDED_OLD_NEW = 'stocksplit_added_old_new',
  RIGHTISSUE_ADDED_OLD_NEW = 'rightissue_added_old_new',
  DIVIDEND_VALUE = 'dividend_value_formatted',
  STOCKSPLIT_RATIO = 'stocksplit_ratio',
  RIGHTISSUE_RATIO = 'rightissue_ratio',
  RIGHTISSUE_PRICE = 'rightissue_price',
  BONUS_RATIO = 'sahambonus_ratio',
  WARRANT_EXC_PRICE = 'wrant_exc_price',
  TENDER_PRICE = 'tender_price',
  RUPS_TIME = 'rups_time',
  PUEXP_TIME = 'puexp_time',
  COMPANY_NAME = 'company_name',
  IPO_PRICE = 'ipo_price',
}

export type TData = keyof Exclude<IData, 'today' | 'timezone'>;

export interface ICalendarStreamline {
  title: string;
  dataSources: Record<string, any>[];
  columns: Record<string, any>[];
  rowKey: string;
}
